import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingCart, Search, Menu, Heart, X } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { OctopusMascot } from "@/components/Mascot";
import { imgUrl } from "@/lib/api";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const NAV = [
  { label: "Início", href: "/" },
  { label: "Produtos", href: "/produtos" },
  { label: "Categorias", href: "/#categorias" },
  { label: "Sobre a Art3D", href: "/#sobre" },
  { label: "Contato", href: "/#contato" },
];

export default function Header() {
  const { content, cartCount, setCartOpen, favorites } = useStore();
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  const submitSearch = (e) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/produtos?q=${encodeURIComponent(query.trim())}`);
      setSearchOpen(false);
      setMenuOpen(false);
    }
  };

  const logo = (
    <Link to="/" className="flex items-center gap-2" data-testid="header-logo">
      {content?.logo_url ? (
        <img src={imgUrl(content.logo_url)} alt={content.brand_name || "Art3D"} className="h-10 w-auto object-contain" />
      ) : (
        <>
          <OctopusMascot size={40} />
          <span className="text-2xl font-black tracking-tight text-slate-900">
            Art<span className="text-gradient">3D</span>
          </span>
        </>
      )}
    </Link>
  );

  return (
    <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 gap-3">
          {logo}

          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            {NAV.map((item) => (
              <a
                key={item.label}
                href={item.href}
                className="px-3.5 py-2 text-sm font-semibold text-slate-600 hover:text-pink-600 rounded-full hover:bg-pink-50 transition-colors duration-200"
              >
                {item.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-1.5">
            <button
              data-testid="search-toggle-btn"
              aria-label="Buscar"
              onClick={() => setSearchOpen((v) => !v)}
              className="w-10 h-10 rounded-full flex items-center justify-center text-slate-500 hover:bg-pink-50 hover:text-pink-600 transition-colors"
            >
              {searchOpen ? <X size={20} /> : <Search size={20} />}
            </button>
            <Link
              to="/favoritos"
              data-testid="favorites-link"
              aria-label="Favoritos"
              className="relative w-10 h-10 rounded-full hidden sm:flex items-center justify-center text-slate-500 hover:bg-pink-50 hover:text-pink-600 transition-colors"
            >
              <Heart size={20} />
              {favorites.length > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-4.5 h-4.5 min-w-[18px] px-0.5 rounded-full bg-purple-500 text-white text-[10px] font-bold flex items-center justify-center">
                  {favorites.length}
                </span>
              )}
            </Link>
            <button
              data-testid="cart-open-btn"
              aria-label="Carrinho"
              onClick={() => setCartOpen(true)}
              className="relative w-10 h-10 rounded-full flex items-center justify-center text-slate-500 hover:bg-pink-50 hover:text-pink-600 transition-colors"
            >
              <ShoppingCart size={20} />
              {cartCount > 0 && (
                <span data-testid="cart-count" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-pink-500 text-white text-[10px] font-bold flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </button>
            <Link
              to="/produtos"
              data-testid="header-buy-btn"
              className="hidden md:inline-flex ml-1 bg-brand-gradient text-white text-sm font-bold px-5 py-2.5 rounded-full shadow-soft transition-all duration-200 hover:opacity-90 active:scale-95"
            >
              Comprar
            </Link>
            <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
              <SheetTrigger asChild>
                <button
                  data-testid="mobile-menu-btn"
                  aria-label="Menu"
                  className="lg:hidden w-10 h-10 rounded-full flex items-center justify-center text-slate-600 hover:bg-pink-50"
                >
                  <Menu size={22} />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-80">
                <div className="flex flex-col gap-2 mt-8">
                  {NAV.map((item) => (
                    <a
                      key={item.label}
                      href={item.href}
                      onClick={() => setMenuOpen(false)}
                      className="px-4 py-3 rounded-xl text-base font-semibold text-slate-700 hover:bg-pink-50 hover:text-pink-600 transition-colors"
                    >
                      {item.label}
                    </a>
                  ))}
                  <Link
                    to="/favoritos"
                    onClick={() => setMenuOpen(false)}
                    className="px-4 py-3 rounded-xl text-base font-semibold text-slate-700 hover:bg-pink-50 hover:text-pink-600"
                  >
                    💗 Meus favoritos
                  </Link>
                  <Link
                    to="/produtos"
                    onClick={() => setMenuOpen(false)}
                    className="mt-3 bg-brand-gradient text-white text-center font-bold px-5 py-3 rounded-full"
                    data-testid="mobile-buy-btn"
                  >
                    Comprar
                  </Link>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>

        {searchOpen && (
          <form onSubmit={submitSearch} className="pb-3 animate-in fade-in slide-in-from-top-1 duration-200" data-testid="header-search-form">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="O que você está procurando? 🔍"
                data-testid="header-search-input"
                className="w-full pl-11 pr-4 py-3 rounded-full border border-slate-200 bg-slate-50 focus:bg-white focus:border-pink-300 focus:ring-2 focus:ring-pink-100 outline-none text-sm transition-all"
              />
            </div>
          </form>
        )}
      </div>
    </header>
  );
}
